import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { authAPI, accessAPI, auditAPI, postSessionEndKeepalive } from '../services/api';
import { formatErrorMessage } from '../utils/errorHandler';
import { SIDEBAR_MENU_HIDDEN_DEFAULT } from '../constants/sidebarMenuItems.js';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function decodeTokenPayload(token) {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    return JSON.parse(atob(parts[1]));
  } catch {
    return null;
  }
}

function buildUserFromToken(token) {
  const payload = decodeTokenPayload(token);
  if (!payload) return null;
  const now = Math.floor(Date.now() / 1000);
  if (payload.exp && payload.exp < now) return null;
  return {
    token,
    id: payload.id,
    username: payload.username,
    email: payload.email,
    role: payload.role,
    permissions: [],
    roles: [],
    scope: { type: 'all', selections: [] },
    isSuperAdmin: false,
  };
}

function mergeAccessProfile(baseUser, accessUser) {
  if (!baseUser || !accessUser) return baseUser;
  return {
    ...baseUser,
    ...accessUser,
    token: baseUser.token,
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [sidebarMenuHidden, setSidebarMenuHidden] = useState(SIDEBAR_MENU_HIDDEN_DEFAULT);
  const [loading, setLoading] = useState(true);
  const loggingOutRef = useRef(false);

  const fetchAccessProfile = useCallback(async (baseUser) => {
    if (!baseUser?.token) return { user: baseUser, sidebarMenuHidden: SIDEBAR_MENU_HIDDEN_DEFAULT };
    try {
      const data = await accessAPI.me();
      const hidden = Array.isArray(data.sidebarMenuHidden)
        ? data.sidebarMenuHidden
        : SIDEBAR_MENU_HIDDEN_DEFAULT;
      return {
        user: mergeAccessProfile(baseUser, data.user),
        sidebarMenuHidden: hidden,
      };
    } catch (err) {
      console.error('Failed to load access profile:', err);
      return { user: baseUser, sidebarMenuHidden: SIDEBAR_MENU_HIDDEN_DEFAULT };
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function initAuth() {
      const token = localStorage.getItem('authToken');
      if (!token) {
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      const baseUser = buildUserFromToken(token);
      if (!baseUser) {
        localStorage.removeItem('authToken');
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      const { user: enriched, sidebarMenuHidden: hidden } = await fetchAccessProfile(baseUser);
      if (!cancelled) {
        setUser(enriched);
        setSidebarMenuHidden(hidden);
        setLoading(false);
      }
    }

    initAuth();
    return () => {
      cancelled = true;
    };
  }, [fetchAccessProfile]);

  useEffect(() => {
    if (!user?.token) return undefined;
    const onPageHide = (e) => {
      if (e.persisted) return;
      if (loggingOutRef.current) return;
      postSessionEndKeepalive('page_leave');
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [user?.token]);

  const applySessionFromAuthResponse = async (response, fallbackUsername) => {
    localStorage.setItem('authToken', response.token);
    const userInfo = response.user || { username: fallbackUsername, role: 'user' };
    const baseUser = {
      token: response.token,
      username: userInfo.username,
      id: userInfo.id,
      email: userInfo.email,
      role: userInfo.role,
      permissions: userInfo.permissions || [],
      roles: userInfo.roles || [],
      scope: userInfo.scope || { type: 'all', selections: [] },
      isSuperAdmin: Boolean(userInfo.isSuperAdmin),
      mfaEnabled: Boolean(userInfo.mfaEnabled),
    };
    const { user: enriched, sidebarMenuHidden: hidden } = await fetchAccessProfile(baseUser);
    setUser(enriched);
    setSidebarMenuHidden(hidden);
  };

  const login = async (username, password) => {
    loggingOutRef.current = false;
    try {
      const response = await authAPI.login(username, password);
      if (response.mfaRequired && response.mfaToken) {
        return {
          success: false,
          mfaRequired: true,
          mfaToken: response.mfaToken,
        };
      }
      await applySessionFromAuthResponse(response, username);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: formatErrorMessage(error),
      };
    }
  };

  const completeMfaLogin = async (mfaToken, code) => {
    loggingOutRef.current = false;
    try {
      const response = await authAPI.verifyMfaLogin(mfaToken, code);
      await applySessionFromAuthResponse(response);
      return { success: true };
    } catch (error) {
      console.error('MFA login error:', error);
      return {
        success: false,
        error: formatErrorMessage(error),
      };
    }
  };

  const logout = async () => {
    loggingOutRef.current = true;
    try {
      await auditAPI.sessionEnd({ reason: 'logout' });
    } catch (error) {
      console.error('Session end audit:', error);
    }
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      setUser(null);
      setSidebarMenuHidden(SIDEBAR_MENU_HIDDEN_DEFAULT);
    }
  };

  const refreshAccess = useCallback(async () => {
    if (!user?.token) return;
    const { user: enriched, sidebarMenuHidden: hidden } = await fetchAccessProfile(user);
    setUser(enriched);
    setSidebarMenuHidden(hidden);
  }, [user, fetchAccessProfile]);

  const permissions = user?.permissions || [];
  const permissionSet = useMemo(() => new Set(permissions), [permissions]);
  const isSuperAdmin = Boolean(user?.isSuperAdmin);

  const can = useCallback(
    (code) => {
      if (!user) return false;
      if (isSuperAdmin) return true;
      return permissionSet.has(code);
    },
    [user, isSuperAdmin, permissionSet],
  );

  const canAny = useCallback(
    (...codes) => {
      if (!user) return false;
      if (isSuperAdmin) return true;
      const flat = codes.flat();
      return flat.some((code) => permissionSet.has(code));
    },
    [user, isSuperAdmin, permissionSet],
  );

  const isAdmin =
    user?.role === 'admin' || isSuperAdmin || permissionSet.has('users.view');

  /** Pull/Push/CSV sync jargon — HotelOnline Administrator + Super Administrator only. */
  const isZraAuditor = useMemo(() => {
    if (!user) return false;
    const roles = user.roles || [];
    return roles.some((r) => (typeof r === 'string' ? r : r?.slug) === 'zra-auditor');
  }, [user]);

  const canSeePmsIntegrationDetails = useMemo(() => {
    if (!user) return false;
    if (isSuperAdmin) return true;
    const roles = user.roles || [];
    return roles.some((r) => (typeof r === 'string' ? r : r?.slug) === 'hotelonline-administrator');
  }, [user, isSuperAdmin]);

  // PMS Suppliers directory/ops only — hotel reports live under /properties/pms/...
  const canBrowsePmsSuppliers =
    canAny('pms_suppliers.view', 'pms_suppliers.manage_integrations') || isSuperAdmin;

  // FTO registry is taxpayer-domain ops, not property report viewing.
  const canBrowseFtoEntities = can('taxpayers.view') || isSuperAdmin;

  const sidebarHiddenSet = useMemo(() => new Set(sidebarMenuHidden || []), [sidebarMenuHidden]);

  /**
   * SuperAdmin bypasses hide overrides. Everyone else: hidden keys win over permissions.
   * @param {string} key
   */
  const isSidebarMenuVisible = useCallback(
    (key) => {
      if (!key) return true;
      if (isSuperAdmin) return true;
      return !sidebarHiddenSet.has(key);
    },
    [isSuperAdmin, sidebarHiddenSet]
  );

  const applySidebarMenuHidden = useCallback((keys) => {
    setSidebarMenuHidden(Array.isArray(keys) ? keys : SIDEBAR_MENU_HIDDEN_DEFAULT);
  }, []);

  const value = {
    user,
    login,
    completeMfaLogin,
    logout,
    refreshAccess,
    loading,
    isAuthenticated: !!user,
    isAdmin,
    isSuperAdmin,
    isZraAuditor,
    canSeePmsIntegrationDetails,
    organization: user?.organization ?? null,
    roles: user?.roles ?? [],
    scopes: user?.scope?.selections ?? [],
    scope: user?.scope ?? { type: 'all', selections: [] },
    permissions,
    can,
    canAny,
    canBrowsePmsSuppliers,
    canBrowseFtoEntities,
    sidebarMenuHidden,
    isSidebarMenuVisible,
    applySidebarMenuHidden,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
